import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useMemo,
} from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Tooltip,
} from "@mui/material";
import useFetchLinkedServices from "../../../hooks/useFetchLinkedServices";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate, useParams } from "react-router-dom";
import { IconViewSvgWhite } from "../../../common/utils/icons";
import { useRuleResourceContext } from "../../../contexts/RuleResourceContext";
import { useToast } from "../../../services/utils";
import ViewResourceData from "../../../components/Resource/ReRun/ViewResourceData";
import EditReRunValidateResourceDataTab from "./EditReRunValidateResourceDataTab";

interface ReRunValidateResourceDataTabProps {
  resourcesData: any;
  isLoading: boolean;
  setResourcesData: Dispatch<SetStateAction<any>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  fileProcessingData: any;
  aggregationBaseResourceData: any;
  setAggregationBaseResourceData: Dispatch<SetStateAction<any>>;
  connectionKeysData: any[];
}

const ReRunValidateResourceDataTab = ({
  resourcesData,
  isLoading: _isLoading,
  setResourcesData,
  setIsLoading,
  fileProcessingData: _fileProcessingData,
  aggregationBaseResourceData,
  setAggregationBaseResourceData,
  connectionKeysData,
}: ReRunValidateResourceDataTabProps) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { domainId } = useParams();
  const { isResourceEdit, fetchedAllConnectionKeys } = useRuleResourceContext();
  const [resourceType, setResourceType] = useState("");

  const [selectSystemId, setSelectSystemId] = useState<number>(0);
  const [expandedAccordion, setExpandedAccordion] =
    useState<any>("resource-data");
  const [linkedServicesData] = useFetchLinkedServices({ setIsLoading });
  // Connection key extraction logic removed - now using props from parent to avoid duplicate API calls
  // Connection keys are now passed as props from parent component to avoid duplicate API calls

  useEffect(() => {
    if (resourcesData) {
      setResourceType(resourcesData?.aggregation_type);
      //setSelectSystemId((prev) => resourcesData?.linked_service_id);
    }
  }, [resourcesData]);

  useEffect(() => {
    if (aggregationBaseResourceData) {
      //setSelectSystemId(aggregationBaseResourceData?.linked_service_id);
    }
  }, [aggregationBaseResourceData]);

  useEffect(() => {
    // Extract system_id from the selected connection key, following the same pattern as child components
    const connectionKeyId =
      resourcesData?.connection_key ||
      aggregationBaseResourceData?.additional_properties?.resource_definition
        ?.connection_key ||
      aggregationBaseResourceData?.connection_key;

    if (connectionKeyId && fetchedAllConnectionKeys?.length > 0) {
      const connectionKey = fetchedAllConnectionKeys.find(
        (ck: any) => ck.id === connectionKeyId
      );
      if (connectionKey?.system_id && connectionKey.system_id > 0) {
        setSelectSystemId(connectionKey.system_id);
      }
    } else if (resourcesData?.system_id && resourcesData.system_id > 0) {
      setSelectSystemId(resourcesData.system_id);
    }
  }, [resourcesData, fetchedAllConnectionKeys, aggregationBaseResourceData]);

  const handleChangeAccordion =
    (panel: string) => (_event: any, isExpanded: any) => {
      if (isResourceEdit !== "") {
        showToast("Please save changes first!!", "warning");
        return;
      }
      setExpandedAccordion(isExpanded ? panel : null);
    };

  const handleViewResource = (
    domainId: any,
    resourceId: any,
    _resourceName: any,
    e: any
  ) => {
    e.preventDefault();
    if (isResourceEdit !== "") {
      return;
    }
    navigate(`/resource/${domainId}/view/${resourceId}`);
  };
  return (
    <>
      {isResourceEdit === "resource" ? (
        <EditReRunValidateResourceDataTab
          resourceData={resourcesData}
          setIsLoading={setIsLoading}
          setResourcesData={setResourcesData}
          linkedServicesData={linkedServicesData}
          connectionKeysData={connectionKeysData}
          setSelectSystemId={setSelectSystemId}
        />
      ) : (
        <Grid container rowSpacing={2.5} columnSpacing={4}>
          <ViewResourceData
            resourceData={resourcesData}
            resourcesData={resourcesData}
            linkedServicesData={linkedServicesData}
            connectionKeysData={connectionKeysData}
            // fileProcessingData={fileProcessingData}
            setIsLoading={setIsLoading}
            showAdditionalResource={true}
            resource_id={resourcesData?.resource_id}
            setResourcesData={setResourcesData}
            resourceCategory={"resource"}
          />
        </Grid>
      )}
      {resourceType === "aggregated" && (
        <Accordion
          expanded={expandedAccordion === "base-resource-data"}
          onChange={handleChangeAccordion("base-resource-data")}
          className="heading-bold mt-6 no-before-shadow color-palette color-palette-2-b"
        >
          <AccordionSummary
            aria-controls="panel1d-content"
            id="panel1d-header"
            expandIcon={<ExpandMoreIcon />}
          >
            <div className="custom-actions">
              <div className="heading">
                Base Resource Data -{" "}
                <span style={{ fontWeight: "normal" }}>
                  {aggregationBaseResourceData?.resource_name}
                </span>
              </div>
              <div className="action-btns">
                <Tooltip title="Navigate to view resource" placement="top">
                  <button
                    className="view-icon"
                    onClick={(e) =>
                      handleViewResource(
                        domainId,
                        aggregationBaseResourceData?.resource_id,
                        aggregationBaseResourceData?.resource_name,
                        e
                      )
                    }
                  >
                    <IconViewSvgWhite />
                  </button>
                </Tooltip>
              </div>
            </div>
          </AccordionSummary>
          <AccordionDetails sx={{ paddingTop: "16px" }}>
            {isResourceEdit === "aggregatedResource" ? (
              <>
                <EditReRunValidateResourceDataTab
                  resourceData={aggregationBaseResourceData}
                  setIsLoading={setIsLoading}
                  setResourcesData={setAggregationBaseResourceData}
                  linkedServicesData={linkedServicesData}
                  connectionKeysData={connectionKeysData}
                  setSelectSystemId={setSelectSystemId}
                />
              </>
            ) : (
              <Grid container rowSpacing={2.5} columnSpacing={4}>
                <ViewResourceData
                  resourceData={aggregationBaseResourceData}
                  resourcesData={aggregationBaseResourceData}
                  linkedServicesData={linkedServicesData}
                  connectionKeysData={connectionKeysData}
                  // fileProcessingData={fileProcessingData}
                  setIsLoading={setIsLoading}
                  showAdditionalResource={true}
                  resource_id={resourcesData?.resource_id}
                  setResourcesData={setAggregationBaseResourceData}
                  resourceCategory={"aggregatedResource"}
                />
              </Grid>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};

export default ReRunValidateResourceDataTab;
